// src/components/LoginComponent.jsx
import React, { useState, useContext } from 'react';
import { AuthContext } from '../auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import '../global.css';

const LoginComponent = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { setToken, setRole, setAccountId } = useContext(AuthContext);
  const navigate = useNavigate();

  // Forgot password modal state
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [codeVerified, setCodeVerified] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setBusy(true);

    const payload = {
      email: (email || '').trim().toLowerCase(),
      password: password || '',
    };

    try {
      const res = await fetch('https://vballbackend.onrender.com/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (res.status === 403 && data?.msg === 'deactivated') {
        alert('Your account has been deactivated. Please contact support for more details.');
        return;
      }

      if (!res.ok) {
        alert('Invalid credentials');
        return;
      }

      setToken(data.access_token);
      setRole?.(data.account_type);
      setAccountId?.(data.account_id);

      navigate(data.account_type === 'coach' ? '/coachdashboard' : `/player/${data.account_id}`);
    } catch (err) {
      alert('Server error. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  // ===== Forgot password flow =====
  const resetForgotState = () => {
    setResetEmail('');
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setCodeVerified(false);
    setStep(1);
  };

  const handleRequestCode = async () => {
    const emailToSend = (resetEmail || '').trim().toLowerCase();
    if (!emailToSend) return alert('Please enter your email address.');

    const res = await fetch('https://vballbackend.onrender.com/api/request-reset', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: emailToSend }),
    });

    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      alert('Reset code sent to your email');
      setStep(2);
    } else {
      alert(data.msg || 'Failed to send reset code');
    }
  };

  const handleVerifyCode = async () => {
    const emailToSend = (resetEmail || '').trim().toLowerCase();
    if (!resetCode) return alert('Enter the 5‑digit code sent to your email.');

    const res = await fetch('https://vballbackend.onrender.com/api/verify-reset-code', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: emailToSend, code: resetCode }),
    });

    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      setCodeVerified(true);
      setStep(3);
    } else {
      alert(data.msg || 'Invalid or expired code');
    }
  };

  const handleResetPassword = async () => {
    const emailToSend = (resetEmail || '').trim().toLowerCase();
    if (!newPassword || !confirmPassword) return alert('Please fill out both password fields.');
    if (newPassword !== confirmPassword) return alert("Passwords don't match.");

    const res = await fetch('https://vballbackend.onrender.com/api/reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: emailToSend, newPassword }),
    });

    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      alert('Password updated! You can now log in.');
      setShowForgotModal(false);
      resetForgotState();
    } else {
      alert(data.msg || 'Error resetting password');
    }
  };

  return (
    <div className="container" style={{ maxWidth: '400px', marginTop: '5rem' }}>
      <div className="card">
        <h2 className="mb-4">Login</h2>

        <form onSubmit={handleLogin} className="flex-col">
          <div className="mb-4">
            <label htmlFor="email" className="text-muted mb-1">Email</label>
            <input
              type="email"
              id="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
            />
          </div>

          <div className="mb-4">
            <label htmlFor="password" className="text-muted mb-1">Password</label>
            <input
              type="password"
              id="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>

          <button type="submit" className="btn-primary mt-2" disabled={busy}>
            {busy ? 'Signing in…' : 'Login'}
          </button>
        </form>

        <div className="mt-3 text-small">
          <button
            onClick={() => setShowForgotModal(true)}
            className="link-button"
            style={{ padding: 0, border: 'none', background: 'none', color: '#3B82F6', cursor: 'pointer' }}
          >
            Forgot Password?
          </button>
        </div>

        <div className="mt-4 text-small">
          Don’t have an account? <a href="/coach-register">Register here</a>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotModal && (
        <div className="modal-overlay">
          <div className="modal-card">
            <h3 className="mb-3">Reset Your Password</h3>

            {step === 1 && (
              <>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="mb-3"
                />
                <button className="btn-primary" onClick={handleRequestCode}>
                  Send Reset Code
                </button>
              </>
            )}

            {step === 2 && (
              <>
                <input
                  type="text"
                  placeholder="Enter the 5-digit code"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  className="mb-3"
                />
                <button className="btn-primary" onClick={handleVerifyCode}>
                  Verify Code
                </button>
              </>
            )}

            {step === 3 && codeVerified && (
              <>
                <input
                  type="password"
                  placeholder="New password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mb-3"
                />
                <input
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mb-3"
                />
                <button className="btn-primary" onClick={handleResetPassword}>
                  Reset Password
                </button>
              </>
            )}

            <button
              className="btn-secondary mt-3"
              onClick={() => {
                setShowForgotModal(false);
                resetForgotState();
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginComponent;
